import { PropsWithChildren, ReactNode, useCallback } from 'react';

import {
  DialogActions,
  DialogContent,
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  Stack,
  Typography,
} from '@mui/material';
import { useBreakpoints } from 'common/hooks';
import { VISUAL_REFRESH } from 'common/utils';
import { Dialog as DsDialog } from 'components';
import { CloseIcon } from 'icons';

import { Button } from '../Buttons';
import { CopyTextField } from '../CopyTextField';
import { DialogCloseButton, DialogContentText, DialogSubtitle, DialogTitle, StyledDialogProps } from './Dialog.styled';
import { toDsProps } from './toDsProps';

type ShareOptions = {
  url: string;
  label?: string;
};

export type DialogProps = Pick<
  MuiDialogProps,
  'fullWidth' | 'maxWidth' | 'role' | 'fullScreen' | 'sx' | 'disableRestoreFocus' | 'scroll'
> & {
  cancelLabel?: string;
  confirmLabel?: string;
  isConfirmLoading?: boolean;
  isConfirmDisabled?: boolean;
  isOpen: boolean;
  title?: ReactNode;
  subtitle?: string;
  description?: ReactNode;
  showCancelButton?: boolean;
  showCloseButton?: boolean;
  severity?: 'default' | 'danger';
  disableClickOutside?: boolean;
  formId?: string;
  shareOptions?: ShareOptions | ShareOptions[];
  /**
   * Typed explicitly because TypeScript waves hyphenated JSX attributes through unchecked, so five
   * call sites have been passing one into the rest spread all along without it appearing in this
   * type. `toDsProps` has to be able to see it — see the note in `components/Dialog/types.ts`.
   */
  'data-testid'?: string;
  onClose?(): void;
  onClosed?(): void;
  onConfirm?(): void;
  Actions?: ReactNode;
} & StyledDialogProps;

export const Dialog = ({
  cancelLabel = 'Cancel',
  children,
  confirmLabel = 'Confirm',
  description,
  fullWidth,
  fullScreen,
  isOpen,
  isConfirmLoading,
  isConfirmDisabled,
  maxWidth = 'md',
  showCancelButton = true,
  showCloseButton = true,
  title,
  subtitle,
  severity = 'default',
  variant,
  centered = variant === 'confirmation',
  disableClickOutside = false,
  formId,
  shareOptions = [],
  Actions,
  onClose,
  onClosed,
  onConfirm,
  ...dialogProps
}: PropsWithChildren<DialogProps>) => {
  const showConfirmButton = !!(onConfirm || formId);
  const showDialogActions = showCancelButton || showConfirmButton || Actions;
  const showDialogContent = description || children;
  const showShareSection = !!shareOptions;
  const shareFields = Array.isArray(shareOptions) ? shareOptions : [shareOptions];
  const { isMobile } = useBreakpoints();

  /**
   * The visual-refresh gate lives HERE, not at the call sites — all forty dialogs in the app reach
   * this component and none of them mention the flag. `toDsProps` returns `null` for anything DS 2.0
   * cannot express, which falls through to the MUI implementation below.
   */
  const dsProps = VISUAL_REFRESH
    ? toDsProps({
        isOpen,
        title,
        subtitle,
        maxWidth,
        fullScreen,
        shareOptions,
        showCloseButton,
        disableClickOutside,
        // Carries `sx`, `scroll`, `role`, `disableRestoreFocus` and `data-testid` — the props this
        // component never reads itself and passes straight to MUI. The mapper refuses `sx`, `scroll`
        // and `role`, maps `disableRestoreFocus` onto `finalFocus`, and forwards `data-testid`.
        ...dialogProps,
      })
    : null;

  /**
   * The DS component reports `open`; the legacy API only has "closed". Opening is driven by the call
   * site setting `isOpen`, so there is nothing to report back for it — which is why this drops the
   * `true` case rather than exposing an `onOpen`.
   */
  const handleOpenChange = useCallback((nextOpen: boolean) => !nextOpen && onClose?.(), [onClose]);

  if (dsProps) {
    return (
      <DsDialog
        {...dsProps}
        description={description}
        /**
         * Built from the SAME legacy `Button`s the MUI branch uses, which refresh themselves through
         * their own wrapper — so the footer needs no DS vocabulary here. Two deliberate departures:
         *
         *   - cancel is `text` rather than `outlined-grey`, because Figma draws the secondary action
         *     borderless (544:20045). `outlined-grey` was the legacy dialog's own choice.
         *   - no `fullWidth`. The legacy footer stretched and stacked its buttons below the `md`
         *     breakpoint; the design's 402px mobile frame (593:39201) draws them side by side and
         *     auto-width, and `dialogFooterRecipe` is right-aligned at every width to match.
         */
        footer={
          showDialogActions ? (
            <>
              {showCancelButton && (
                <Button variant="text" onClick={onClose} disabled={isConfirmLoading}>
                  {cancelLabel}
                </Button>
              )}
              {showConfirmButton && (
                <Button
                  variant="contained"
                  color={severity === 'danger' ? 'error' : 'primary'}
                  isLoading={isConfirmLoading}
                  onClick={formId ? undefined : onConfirm}
                  type={formId ? 'submit' : undefined}
                  form={formId}
                  disabled={isConfirmDisabled}
                >
                  {confirmLabel}
                </Button>
              )}
              {Actions}
            </>
          ) : undefined
        }
        open={isOpen}
        onOpenChange={handleOpenChange}
        // The legacy prop is MUI's transition callback, which the DS dialog has no equivalent of by
        // that name; Base UI's `onOpenChangeComplete` fires at the same point in the lifecycle.
        onOpenChangeComplete={onClosed ? (open) => !open && onClosed() : undefined}
      >
        {children}
      </DsDialog>
    );
  }

  return (
    <MuiDialog
      open={isOpen}
      onClose={disableClickOutside ? undefined : onClose}
      disableEscapeKeyDown={disableClickOutside}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      onTransitionExited={onClosed}
      onClick={(e) => {
        e.stopPropagation();
      }}
      {...dialogProps}
    >
      <DialogTitle id="dialog-title" centered={centered} fullScreen={fullScreen}>
        {title}
        {showCloseButton && (
          <DialogCloseButton onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="medium" />
          </DialogCloseButton>
        )}
      </DialogTitle>
      {subtitle && <DialogSubtitle pb={2}>{subtitle}</DialogSubtitle>}
      {showDialogContent && (
        <DialogContent data-testid="dialog-content">
          {description && (
            <DialogContentText id="dialog-description" centered={centered}>
              {description}
            </DialogContentText>
          )}

          {children}

          {showShareSection && shareFields && (
            <Stack gap={3} data-testid="share-section">
              {shareFields.map((option, index) => (
                <Stack key={index} gap={1} mt={option.label ? 0 : 2}>
                  {option.label && (
                    <Typography variant="natter-text-sm" fontWeight={600}>
                      {option.label}
                    </Typography>
                  )}
                  <CopyTextField value={option.url} />
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
      )}
      {showDialogActions && (
        <DialogActions
          sx={({ breakpoints }) => ({
            [breakpoints.down('md')]: {
              flexDirection: 'column-reverse',
              gap: 2,
              '&>button': { marginLeft: '0 !important' },
            },
            // Buttons passed through `Actions` stretch where the built-in ones get `fullWidth`
            // (`isMobile`), so a stacked footer never mixes full-width and intrinsic-width buttons.
            [breakpoints.down('sm')]: {
              '&>.MuiButton-root': { width: '100%' },
            },
          })}
        >
          {showCancelButton && (
            <Button
              variant="outlined-grey"
              color="secondary"
              onClick={onClose}
              fullWidth={centered || isMobile}
              disabled={isConfirmLoading}
            >
              {cancelLabel}
            </Button>
          )}
          {showConfirmButton && (
            <Button
              variant="contained"
              color={severity === 'danger' ? 'error' : 'primary'}
              isLoading={isConfirmLoading}
              fullWidth={centered || isMobile}
              onClick={formId ? undefined : onConfirm}
              type={formId ? 'submit' : undefined}
              form={formId}
              disabled={isConfirmDisabled}
            >
              {confirmLabel}
            </Button>
          )}
          {Actions}
        </DialogActions>
      )}
    </MuiDialog>
  );
};
